/**
 * Shared Axios instance for the Django API with JWT attach + refresh-on-401 handling.
 *
 * Where: imported by every `ui/src/api/*.ts` module.
 * Uses: `localStorage` for access/refresh tokens; `setTokens` / `clearTokens` from auth flows.
 * Contract: base URL `API_BASE` + relative paths from callers; successful responses are `AxiosResponse` — each API module documents whether payload is `response.data` or `response.data.output`.
 * Behavior: attaches `Authorization: Bearer <access>`; on 401 attempts one refresh via POST `{API_BASE}/token/refresh/` with refresh token; failed refresh clears tokens and sets `window.location.href` to `/login`.
 * Invariants: refresh uses standalone `axios` (not `apiClient`) to avoid interceptor loops; `_retry` flag prevents infinite retry on original request.
 */

import axios, { type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';

const API_BASE = 'https://selblockly-backend.mirrorthink.ai/api'; //TODO:

// Create axios instance
export const apiClient = axios.create({
  baseURL: API_BASE,
  timeout: 100000,
  withCredentials: true, // Enable sending cookies with cross-origin requests
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management
let accessToken: string | null = localStorage.getItem('accessToken');
let refreshToken: string | null = localStorage.getItem('refreshToken');

export const setTokens = (access: string, refresh: string): void => {
  accessToken = access;
  refreshToken = refresh;
  localStorage.setItem('accessToken', access);
  localStorage.setItem('refreshToken', refresh);
};

export const clearTokens = (): void => {
  accessToken = null;
  refreshToken = null;
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
};

export const getAccessToken = (): string | null => accessToken;

// Request interceptor to add authorization header
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for token refresh
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (refreshToken) {
        try {
          const response = await axios.post(
            `${API_BASE}/token/refresh/`,
            {},
            {
              withCredentials: true, // Enable sending cookies with cross-origin requests
              headers: {
                Authorization: `Bearer ${refreshToken}`,
              },
            }
          );

          const { access } = response.data;
          setTokens(access, refreshToken);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${access}`;
          return apiClient(originalRequest);
        } catch (refreshError) {
          // Refresh failed, clear tokens and redirect to login
          clearTokens();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token, redirect to login
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// LinkedIn account connection
export const getLinkedInAuthUrl = async (): Promise<string> => {
  const response = await apiClient.post('/unipile/auth-url/');
  return response.data.output;
};
